import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';

type UseResetInboxOnFocusOptions = {
  selectedChatId: string | undefined;
  onReset: () => void;
};

/**
 * Resets the web inbox when it gains focus with no chat open, so arriving on the
 * inbox starts from an unfiltered list.
 *
 * Runs on focus only. The chat id is read through a ref because `useFocusEffect`
 * re-runs whenever its callback changes, and a callback keyed on the chat id
 * fired again the moment a chat closed. Picking a cancer type closes the open
 * chat, so that re-run wiped the filter just chosen.
 */
export function useResetInboxOnFocus({ selectedChatId, onReset }: UseResetInboxOnFocusOptions) {
  const selectedChatIdRef = useRef(selectedChatId);

  const onResetRef = useRef(onReset);

  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;

    onResetRef.current = onReset;
  });

  useFocusEffect(
    useCallback(() => {
      if (isWeb && !selectedChatIdRef.current) {
        onResetRef.current();
      }
    }, [])
  );
}
